import {
  AbstractControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { environment } from '../../../environments/environment';
import { invalidEmailStrings } from './invalid-strings.utils';

export class MotosFormsUtils {
  static phonePattern = environment.PATTERN_CELULAR;
  static platePattern = environment.PATTERN_PLACA;

  static isValidField(form: FormGroup, fieldName: string): boolean | null {
    return form.controls[fieldName].errors && form.controls[fieldName].touched;
  }
}

/**
 * Validador personalizado para correos electrónicos.
 * Implementa múltiples validaciones según reglas predefinidas.
 */
export function emailValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    // Si el control no tiene valor, no se valida. La responsabilidad de requerirlo
    // es del validador `Validators.required`.
    if (!control.value) {
      return null;
    }

    // Criterio: Eliminar espacios al inicio y final.
    const email = String(control.value).trim();

    // Criterio: No debe tener espacios entre caracteres.
    if (/\s/.test(email)) {
      return { containsSpaces: 'El correo no puede contener espacios internos.' };
    }

    // Criterio: Debe cumplir con un formato general (contener un único '@').
    const parts = email.split('@');
    if (parts.length !== 2) {
      return { invalidFormat: 'El formato del correo es incorrecto (debe contener un solo @).' };
    }

    const [username, domain] = parts;
    if (!username || !domain) {
        return { invalidFormat: 'El correo debe tener un usuario y un dominio.' };
    }

    // --- Validación del Nombre de Usuario (antes del @) ---

    // Criterio: Puede contener letras, números, punto (.), guion medio (-) y guion bajo (_).
    if (!/^[a-zA-Z0-9._-]+$/.test(username)) {
      return { invalidUsername: 'El nombre de usuario contiene caracteres no permitidos.' };
    }

    // Criterio: Justo antes del @ solo se permiten letras y números.
    if (!/^[a-zA-Z0-9]$/.test(username.slice(-1))) {
      return { invalidUsernameEnd: 'El nombre de usuario debe terminar con una letra o un número.' };
    }

    // --- Validación del Dominio (después del @) ---

    // Criterio: No se permite un punto (.) inmediatamente después del @.
    if (domain.startsWith('.')) {
      return { dotAfterAt: 'No se permite un punto inmediatamente después del @.' };
    }
    
    // Criterio: El dominio debe contener al menos un punto (.).
    if (!domain.includes('.')) {
        return { noDotInDomain: 'El dominio debe contener al menos un punto (ej: servidor.com).' };
    }

    const domainParts = domain.split('.');
    // Criterio: No se permite que .com, .gov.co, .co aparezcan sin nombre de servidor.
    // Esto se cubre validando que exista un nombre de servidor.
    if (domainParts.length < 2 || !domainParts[0]) {
        return { missingServerName: 'Se requiere un nombre de servidor antes del dominio (ej: servidor.com).' };
    }

    const tld = domainParts[domainParts.length - 1]; // Top-Level Domain (ej: 'com')
    const serverName = domainParts.slice(0, -1).join('.'); // Nombre del servidor (ej: 'gmail')

    // Criterio: El nombre del servidor puede contener letras, números y guion medio.
    // Se valida cada parte del servidor en caso de subdominios (ej: 'mail.google').
    const serverSubParts = serverName.split('.');
    for (const part of serverSubParts) {
        if (!/^[a-zA-Z0-9-]+$/.test(part)) {
            return { invalidServer: 'El nombre del servidor contiene caracteres no permitidos.' };
        }
    }
    
    // Criterio: El TLD deben ser letras de 2 a 3 caracteres o "AERO".
    if (tld.toLowerCase() !== 'aero' && !/^[a-zA-Z]{2,3}$/.test(tld)) {
      return { invalidTld: 'El dominio final (TLD) debe ser de 2 a 3 letras o "aero".' };
    }

    // Si todas las validaciones pasan, el correo es válido.
    return null;
  };
}
/**
 * Valida si el correo está contenido en una lista de correos no permitidos.
 */
export function restrictedEmailsValidator(): ValidatorFn {
  const normalizedEmails = invalidEmailStrings.map((email) =>
    email.toLowerCase().trim()
  );
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null; // No hay valor, no es inválido (el 'required' debe manejarse aparte)
    }
    const email = control.value.toLowerCase().trim();
    return normalizedEmails.includes(email)
      ? { restrictedEmail: 'El correo proporcionado no está permitido.' }
      : null;
  };
}

/**
 * Validador: Garantiza que un número tiene exactamente 10 dígitos.
 */
export function exactLengthValidator(length: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.hasValidator(Validators.required)) {
      return null;
    }
    return control.value?.toString()?.length === length
      ? null
      : { exactLength: `El número debe tener exactamente ${length} dígitos.` };
  };
}

/**
 * Validador: Verifica que el número comience con un rango indicativo válido (300 a 350).
 */
export function startsWithValidatorNumber(
  min: number,
  max: number
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value?.toString()
    if (!control.hasValidator(Validators.required)) {
      return null;
    }
    if (value.length < 3) {
      return null; // Si hay menos de 3 dígitos, no validar
    }
    const prefix = parseInt(value.substring(0, 3), 10);
    return prefix >= min && prefix <= max
      ? null
      : {
          startsWith: `El número debe comenzar con un indicativo entre ${min} y ${max}.`,
        };
  };
}


/**
 * Validador que verifica si el valor del control contiene únicamente dígitos.
 */
export function numericValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    // Si no hay valor, no se aplica esta validación. 
    // `Validators.required` se encarga de los campos vacíos.
    if (!control.value) {
      return null;
    }

    // La expresión regular /^\d+$/ comprueba que el string, de principio a fin,
    // contenga uno o más dígitos, y nada más.
    const isValid = /^\d+$/.test(control.value);
    
    // Si la prueba falla, devuelve un objeto de error.
    return isValid ? null : { numeric: 'Este campo solo debe contener dígitos numéricos.' };
  };
}